<template>
  <v-container fluid>
    <base-material-card color="primary" icon="mdi-file-upload" inline class="ml-0 px-5 py-4">
      <template v-slot:after-heading>
        <div class="display-2 font-weight-light">Importador de Dados</div>
      </template>
      <v-btn
        id="btn-nova-importador-dados"
        color="success"
        dark
        elevation="1"
        class="mt-5"
        style="float: left"
        relative
        text
        medium
        @click="$router.push('novo')"
      >
        <v-icon left size="30px">mdi-plus-circle</v-icon>
        Nova Importação
      </v-btn>
      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        class="break-search ml-auto mt-4"
        label="Procurar"
        hide-details
        single-line
        style="max-width: 250px"
      />
      <v-divider class="mt-10" />
      <v-data-table
        :headers="headers"
        :items="items"
        :search.sync="search"
        :header-props="{ sortByText: 'Ordenar Por' }"
        :footer-props="{ itemsPerPageText: 'Itens por Página' }"
        no-data-text="Nenhuma importação disponível"
        no-results-text="Nenhum registro correspondente encontrado"
        :loading="loading"
      >
        <template v-slot:[`item.nome`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <div class="text-truncate-column" v-bind="attrs" v-on="on">{{ item.nome }}</div>
            </template>
            <span>{{ item.nome }}</span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.created`]="{ item }">
          {{ item.created | parseToDateTimeBR }}
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <general-status :status="item.status" />
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            v-if="['SUCESSO', 'FALHA'].includes(item.status)"
            id="action-detalhes-importador-dados"
            min-width="0"
            class="px-1"
            fab
            icon
            x-small
            @click="abrirDetalhes(item)"
          >
            <v-icon small>mdi-magnify</v-icon>
          </v-btn>
          <v-btn
            v-if="temErros(item)"
            id="action-erros-importador-dados"
            min-width="0"
            class="ml-n1 px-1"
            fab
            icon
            x-small
            title="Baixar arquivo original e linhas com erro (ZIP)"
            @click="downloadErros(item.id)"
          >
            <v-icon small>mdi-file-alert-outline</v-icon>
          </v-btn>
          <v-menu v-if="['SUCESSO', 'FALHA'].includes(item.status) || canDeleteItem(item)" offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                id="action-more-importador-dados"
                color="white"
                class="ml-1"
                height="22px"
                width="22px"
                fab
                x-small
                elevation="1"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon small>mdi mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item v-if="['SUCESSO', 'FALHA'].includes(item.status)" @click="downloadLogsTecnicos(item.id)">
                <v-list-item-title>
                  <v-icon small>mdi-file-document-outline</v-icon>
                  Baixar logs
                </v-list-item-title>
              </v-list-item>
              <v-list-item v-if="canDeleteItem(item)" @click="openDialogDelete(item.id, item.nome)">
                <v-list-item-title>
                  <v-icon small>mdi-trash-can-outline</v-icon>
                  Deletar
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
        <template v-slot:[`footer.page-text`]>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon color="primary" v-bind="attrs" v-on="on" @click="$emit('rechargeTable')">mdi-refresh</v-icon>
            </template>
            <span>Clique aqui para recarregar as importações</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </base-material-card>
    <dialog-delete
      :show-dialog="dialogDelete"
      @changeValue="dialogDelete = $event"
      @deleteTriggered="deletarItem(dialogDeleteData.id)"
    >
      <template slot="title">Deletar Importação #{{ dialogDeleteData.id }}</template>
      <template slot="body">
        Tem certeza que deseja deletar a importação
        <strong>{{ dialogDeleteData.titulo }}</strong>
        ?
      </template>
    </dialog-delete>
    <v-dialog v-model="dialogDetalhesResultado" max-width="450px">
      <v-card>
        <v-card-title class="headline">Resultado da Importação</v-card-title>
        <v-card-text>
          <v-simple-table dense>
            <template v-slot:default>
              <tbody>
                <tr>
                  <td>Total de linhas:</td>
                  <td>{{ resultado.total }}</td>
                </tr>
                <tr>
                  <td>Inseridas:</td>
                  <td>{{ resultado.inseridas }}</td>
                </tr>
                <tr>
                  <td>Atualizadas:</td>
                  <td>{{ resultado.atualizadas }}</td>
                </tr>
                <tr>
                  <td>Erros:</td>
                  <td>{{ resultado.erros }}</td>
                </tr>
                <tr v-if="resultado.mensagem_erro && $store.getters.userIsAdmin">
                  <td>Erro:</td>
                  <td>{{ resultado.mensagem_erro }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="dialogDetalhesResultado = false">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
  import ImportadorDadosService from '@/services/ImportadorDadosService';
  import powerupsActionsMixins from '@/mixins/powerupsActionsMixins';
  import dateFormatterMixins from '@/mixins/dateFormatterMixins';

  export default {
    components: {
      GeneralStatus: () => import('@/components/general/GeneralStatus.vue'),
      DialogDelete: () => import('@/components/general/DialogDelete')
    },
    mixins: [powerupsActionsMixins, dateFormatterMixins],

    props: {
      items: {
        type: Array,
        default: () => []
      },
      loading: {
        type: Boolean,
        required: true
      }
    },

    emits: ['rechargeTable'],

    data: () => ({
      powerupService: ImportadorDadosService,
      powerupEntityName: 'Importador de Dados',
      search: '',
      dialogDetalhesResultado: false,
      resultado: {},
      headers: [
        { text: '#', value: 'id' },
        { text: 'Nome', value: 'nome' },
        { text: 'Layout', value: 'layout_nome' },
        { text: 'Usuário', value: 'usuario.nome' },
        { text: 'Criado em', value: 'created' },
        { text: 'Status', value: 'status' },
        { sortable: false, text: 'Ações', value: 'actions', class: 'pl-4' }
      ]
    }),

    methods: {
      temErros(item) {
        return item.resultado ? JSON.parse(item.resultado).erros > 0 : false;
      },
      downloadErros(id) {
        this.powerupService
          .baixarErros(id)
          .then((res) => {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `importacao-${id}-erros.zip`);
            document.body.appendChild(link);
            link.click();
          })
          .catch(() => {
            this.$toast.error('Falha no download do arquivo de erros.', '', { position: 'topRight' });
          });
      },
      abrirDetalhes(item) {
        this.resultado = item.resultado ? JSON.parse(item.resultado) : {};
        this.dialogDetalhesResultado = true;
      }
    }
  };
</script>
